import { Popover } from "@base-ui/react/popover";
import { Check, ChevronDown } from "lucide-react";

const WEEKDAYS = [
  { value: 0, label: "Monday", shortLabel: "Mon" },
  { value: 1, label: "Tuesday", shortLabel: "Tue" },
  { value: 2, label: "Wednesday", shortLabel: "Wed" },
  { value: 3, label: "Thursday", shortLabel: "Thu" },
  { value: 4, label: "Friday", shortLabel: "Fri" },
  { value: 5, label: "Saturday", shortLabel: "Sat" },
  { value: 6, label: "Sunday", shortLabel: "Sun" },
] as const;

interface WeekdayDropdownProps {
  value?: number[];
  onChange: (value: number[] | undefined) => void;
}

export function WeekdayDropdown({ value, onChange }: WeekdayDropdownProps) {
  const selected = [...(value ?? [])].sort((a, b) => a - b);
  const everyDay = selected.length === 0 || selected.length === WEEKDAYS.length;
  const summary = everyDay
    ? "Every day"
    : WEEKDAYS.filter((day) => selected.includes(day.value))
        .map((day) => day.shortLabel)
        .join(", ");

  function toggleDay(day: number) {
    const next = selected.includes(day)
      ? selected.filter((selectedDay) => selectedDay !== day)
      : [...selected, day].sort((a, b) => a - b);
    onChange(next.length === 0 || next.length === WEEKDAYS.length ? undefined : next);
  }

  return (
    <Popover.Root>
      <Popover.Trigger
        className="flex h-9 w-full max-w-sm items-center justify-between rounded border bg-background px-3 text-sm outline-none transition-colors hover:bg-muted/40 focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30"
        aria-label="Select weekdays"
      >
        <span className="truncate">{summary}</span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner sideOffset={4} align="start" className="z-50">
          <Popover.Popup
            className="w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-none"
            initialFocus={false}
          >
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={everyDay}
              className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
              onClick={() => onChange(undefined)}
            >
              <span className="flex size-4 items-center justify-center">
                {everyDay && <Check className="size-4" aria-hidden="true" />}
              </span>
              Every day
            </button>
            <div className="my-1 h-px bg-border" />
            {WEEKDAYS.map((day) => {
              const checked = !everyDay && selected.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={checked}
                  className="flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                  onClick={() => toggleDay(day.value)}
                >
                  <span className="flex size-4 items-center justify-center">
                    {checked && <Check className="size-4" aria-hidden="true" />}
                  </span>
                  {day.label}
                </button>
              );
            })}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
